import { useMemo, useState, type ReactNode } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import PosterCard from "@/components/base/PosterCard";
import {
  allGenres,
  allRegions,
  allYears,
  defaultFilterOptions,
  filterTitles,
} from "@/lib/media";
import type { FilterOptions } from "@/lib/media";

interface CatalogViewProps {
  type: "movie" | "tv";
  title: string;
  intro: string;
  breadcrumbLabel: string;
}

const sortOptions: { value: FilterOptions["sort"]; label: string }[] = [
  { value: "latest", label: "最新" },
  { value: "hot", label: "热门" },
  { value: "rating", label: "评分" },
];

export default function CatalogView({ type, title, intro, breadcrumbLabel }: CatalogViewProps) {
  const [options, setOptions] = useState<FilterOptions>(defaultFilterOptions);
  const [visible, setVisible] = useState(8);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const results = useMemo(() => filterTitles(type, options), [type, options]);
  const shown = results.slice(0, visible);
  const hasMore = visible < results.length;

  const toggleGenre = (g: string) => {
    setOptions((o) => ({
      ...o,
      genres: o.genres.includes(g) ? o.genres.filter((x) => x !== g) : [...o.genres, g],
    }));
    setVisible(8);
  };

  const toggleRegion = (r: string) => {
    setOptions((o) => ({
      ...o,
      regions: o.regions.includes(r) ? o.regions.filter((x) => x !== r) : [...o.regions, r],
    }));
    setVisible(8);
  };

  const setYear = (y: "all" | number) => {
    setOptions((o) => ({ ...o, year: y }));
    setVisible(8);
  };

  const setStatus = (s: FilterOptions["status"]) => {
    setOptions((o) => ({ ...o, status: s }));
    setVisible(8);
  };

  const setSort = (sort: FilterOptions["sort"]) => {
    setOptions((o) => ({ ...o, sort }));
  };

  const reset = () => {
    setOptions(defaultFilterOptions);
    setVisible(8);
  };

  const filterPanel = (
    <div className="space-y-4">
      <FilterRow label="类型">
        <Chip active={options.genres.length === 0} onClick={() => setOptions((o) => ({ ...o, genres: [] }))}>
          全部
        </Chip>
        {allGenres.map((g) => (
          <Chip key={g} active={options.genres.includes(g)} onClick={() => toggleGenre(g)}>
            {g}
          </Chip>
        ))}
      </FilterRow>

      <FilterRow label="地区">
        <Chip active={options.regions.length === 0} onClick={() => setOptions((o) => ({ ...o, regions: [] }))}>
          全部
        </Chip>
        {allRegions.map((r) => (
          <Chip key={r} active={options.regions.includes(r)} onClick={() => toggleRegion(r)}>
            {r}
          </Chip>
        ))}
      </FilterRow>

      <FilterRow label="年份">
        <Chip active={options.year === "all"} onClick={() => setYear("all")}>
          全部
        </Chip>
        {allYears.slice(0, 8).map((y) => (
          <Chip key={y} active={options.year === y} onClick={() => setYear(y)}>
            {y}
          </Chip>
        ))}
      </FilterRow>

      {type === "tv" && (
        <FilterRow label="状态">
          {(["all", "连载", "完结"] as const).map((s) => (
            <Chip key={s} active={options.status === s} onClick={() => setStatus(s)}>
              {s === "all" ? "全部" : s}
            </Chip>
          ))}
        </FilterRow>
      )}
    </div>
  );

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <PageBreadcrumb items={[{ label: "首页", to: "/" }, { label: breadcrumbLabel }]} />
        <div className="mt-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading">
            {title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            {intro}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-equalizer-line" />
            筛选条件
          </div>
          <button
            type="button"
            onClick={() => setMobileFilterOpen(true)}
            className="md:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs md:text-sm text-foreground-500">排序</span>
            <div className="flex items-center gap-1 bg-background-50 border border-background-200 rounded-full p-1">
              {sortOptions.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSort(s.value)}
                  className={`px-3 py-1 text-xs md:text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    options.sort === s.value
                      ? "bg-primary-500 text-background-50 font-semibold"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hidden md:block bg-background-50 border border-background-200 rounded-lg p-5 mb-6">
          {filterPanel}
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部内容
          </p>
          {(options.genres.length > 0 ||
            options.regions.length > 0 ||
            options.year !== "all" ||
            options.status !== "all") && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-xs md:text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        {results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200 rounded-lg">
            <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-search-eye-line text-3xl text-foreground-400" />
            </span>
            <h3 className="text-base font-semibold text-foreground-800">没有找到符合条件的内容</h3>
            <p className="mt-1 text-sm text-foreground-500">试试放宽筛选条件，或重置后重新浏览</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {shown.map((item) => (
                <PosterCard key={item.id} item={item} />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + 8)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-down-line" />
                  加载更多（剩余 {results.length - visible} 部）
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {mobileFilterOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="relative w-full max-h-[80vh] overflow-y-auto bg-background-50 rounded-t-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-foreground-950 font-heading">筛选条件</h3>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                aria-label="关闭筛选"
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-600" />
              </button>
            </div>
            {filterPanel}
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={reset}
                className="flex-1 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="flex-1 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="shrink-0 w-12 text-sm text-foreground-500 leading-7">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
  muted = false,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 text-xs md:text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
        active
          ? "bg-primary-500 text-background-50 font-medium"
          : muted
            ? "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
            : "bg-background-100 text-foreground-600 hover:bg-background-200"
      }`}
    >
      {children}
    </button>
  );
}